import axios from 'axios'

import request from '@/Network/Requests/request'
import { unwrapBlobError } from '@/Network/Requests/blobError'
import Service from '@/Network/Services/Service'
import type {
  PriorityAttackQueueQuery,
  PriorityContactPayload,
  PriorityPaymentsExportQuery,
  PriorityPaymentsQuery
} from '@/Network/Types/Requests/PriorityPaymentsRequests'
import type {
  PriorityAttackQueueResponse,
  PriorityClaimResponse,
  PriorityContactResponse,
  PriorityFilterOptionsResponse,
  PriorityPaymentsResponse
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/**
 * Recorte -> query string. Só campos preenchidos sobem, e arrays viram CSV
 * (`types=IN_CASH,ANTECIPE`) — mesmo formato de
 * `CheckValidationService.buildExportQuery`. Os DTOs do backend aceitam os
 * arrays tanto em CSV quanto com a chave repetida (`types=A&types=B`); o web
 * manda sempre CSV. Listagem, export e fila de ataque usam ESTA função:
 * divergir aqui faria a tela prometer um recorte e exportar outro.
 */
/** Texto: sobe só se não vazio. */
const TEXT_PARAMS = [
  'startAt',
  'endAt',
  'cycle',
  'tab',
  'slaBand',
  'stationDateFrom',
  'stationDateTo',
  'checkStatus',
  'search',
  'orderBy'
] as const satisfies readonly (keyof PriorityAttackQueueQuery)[]
/** Arrays: sobem como CSV, só se não vazios. */
const LIST_PARAMS = [
  'types',
  'slaState',
  'doctorIds',
  'situations',
  'schedules',
  'healthUnits',
  'sectors',
  'positions',
  'operationalGroups',
  'ids'
] as const satisfies readonly (keyof PriorityAttackQueueQuery)[]
/** Booleanos e números: sobem sempre que definidos — `false` e `0` também. */
const SCALAR_PARAMS = [
  'onlyCritical',
  'onlyOverdue',
  'includeOverdue',
  'minRejections',
  'doctorWithoutUser',
  'stalledHours',
  'page',
  'limit'
] as const satisfies readonly (keyof PriorityAttackQueueQuery)[]

export const buildPriorityPaymentsParams = (
  query: PriorityPaymentsQuery | PriorityAttackQueueQuery
): Record<string, string> => {
  const params: Record<string, string> = {}
  const q = query as PriorityAttackQueueQuery

  for (const key of TEXT_PARAMS) {
    const value = q[key]
    if (value) params[key] = value
  }
  for (const key of LIST_PARAMS) {
    const value = q[key]
    if (value?.length) params[key] = value.join(',')
  }
  for (const key of SCALAR_PARAMS) {
    const value = q[key]
    if (value !== undefined && value !== null) params[key] = String(value)
  }

  return params
}

/** Contrato: `docs/discovery/priority-payments/2026-09-21-api-contract.md`. */
export default class PriorityPaymentsService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/priority-payments'
  }

  /** 409 = outra pessoa tem marca válida no plantão (o ataque pula para o próximo). */
  static isClaimConflict(error: unknown): boolean {
    // eslint-disable-next-line no-restricted-properties -- detecta o 409 no erro cru, antes do handleError
    return axios.isAxiosError(error) && error.response?.status === 409
  }

  async list(query: PriorityPaymentsQuery): Promise<PriorityPaymentsResponse> {
    try {
      const { data } = await request.get<PriorityPaymentsResponse>(
        this.getCompletePath(),
        buildPriorityPaymentsParams(query)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os pagamentos prioritários')
    }
  }

  /** Frente G: opções dos combos Setor e Posição (mesmo escopo da listagem). */
  async filterOptions(): Promise<PriorityFilterOptionsResponse> {
    try {
      const { data } = await request.get<PriorityFilterOptionsResponse>(
        this.getCompletePath('/filter-options')
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar setores e posições')
    }
  }

  /**
   * Download do xlsx no padrão já usado no painel (`CheckValidationService`):
   * `responseType: 'blob'`, nome do `content-disposition`, âncora temporária.
   * O erro vem dentro do Blob — sem `unwrapBlobError` a explicação do backend
   * (ex.: período maior que 31 dias) nunca chegaria ao usuário.
   */
  async exportXlsx(query: PriorityPaymentsExportQuery): Promise<void> {
    try {
      // Contrato: o export não é paginado — leva o recorte inteiro, nunca a página da tela.
      const params = buildPriorityPaymentsParams(query)
      delete params.page
      delete params.limit
      // A2 (25/09): com `list` o BE exporta só a lista fixa e ignora o resto do recorte.
      if (query.list) params.list = query.list
      const { data, headers } = await request.get<BlobPart>(
        this.getCompletePath('/export'),
        params,
        undefined,
        'blob'
      )
      const disposition = (headers?.['content-disposition'] as string | undefined) ?? ''
      const filename =
        disposition.match(/filename="?([^"]+)"?/)?.[1] ?? 'pagamentos-prioritarios.xlsx'
      const url = window.URL.createObjectURL(new Blob([data], { type: XLSX_CONTENT_TYPE }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível exportar os pagamentos prioritários'
      )
    }
  }

  /**
   * Ordem do servidor (`compareBySla`, spec §3.9): pendentes por prazo → crítico
   * → fim do plantão; inclui `AWAITING_AUDIT` ("Auditar agora").
   */
  async attackQueue(query: PriorityAttackQueueQuery): Promise<number[]> {
    try {
      const { data } = await request.get<PriorityAttackQueueResponse>(
        this.getCompletePath('/attack-queue'),
        buildPriorityPaymentsParams(query)
      )
      return data.ids
    } catch (e) {
      throw this.handleError(e, 'Não foi possível montar a fila de ataque')
    }
  }

  async claim(doctorStationId: number): Promise<PriorityClaimResponse> {
    try {
      const { data } = await request.post<PriorityClaimResponse>(
        this.getCompletePath(`/${doctorStationId}/claim`),
        {}
      )
      return data
    } catch (e) {
      // 409 sobe cru para o chamador decidir (o ataque pula; a lista avisa).
      if (PriorityPaymentsService.isClaimConflict(e)) throw e
      throw this.handleError(e, 'Não foi possível marcar o plantão como em tratativa')
    }
  }

  async releaseClaim(doctorStationId: number): Promise<void> {
    try {
      await request.delete(this.getCompletePath(`/${doctorStationId}/claim`))
    } catch (e) {
      throw this.handleError(e, 'Não foi possível soltar a marca de tratativa')
    }
  }

  async registerContact(
    doctorStationId: number,
    payload: PriorityContactPayload
  ): Promise<PriorityContactResponse> {
    try {
      const { data } = await request.post<PriorityContactResponse>(
        this.getCompletePath(`/${doctorStationId}/contacts`),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível registrar o contato')
    }
  }
}
